// ── TextImport — «Импорт текста» в плеере ────────────────────────────────────
// Вставили строки «слово, перевод» (можно с таймкодом в начале: «0:30 cat, кот»)
// — получили карточки. Лист снизу на телефоне, окно на компьютере.

import { useState } from 'react';
import { t } from '../i18n';
import { nCards } from '../utils';
import Icon from '../Icon';
import { Sheet, Field, Chip, Button, usePalette } from '../ui';

// «0:30 word, translation» → { word, translation, time }
export function parseImportText(text, sep) {
  if (!text.trim()) return [];
  return text.trim().split('\n').filter((l) => l.trim()).map((line) => {
    const timeMatch = line.match(/^(\d{1,2}:\d{2}(?:\.\d+)?)\s+/);
    let rest = line, time = null;
    if (timeMatch) { const p = timeMatch[1].split(':'); time = parseInt(p[0], 10) * 60 + parseFloat(p[1]); rest = line.slice(timeMatch[0].length); }
    const idx = rest.indexOf(sep);
    if (idx === -1) return { word: rest.trim(), translation: '', time };
    return { word: rest.substring(0, idx).trim(), translation: rest.substring(idx + sep.length).trim(), time };
  }).filter((c) => c.word);
}

const caps = (C) => ({ fontSize: 11, fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 8 });

export default function TextImport({ onImport, onClose }) {
  const C = usePalette();
  const [text, setText] = useState('');
  const [sep, setSep] = useState('comma');
  const [customSep, setCustomSep] = useState('');
  const parsed = parseImportText(text, sep === 'tab' ? '\t' : sep === 'comma' ? ',' : customSep || '\t');

  return (
    <Sheet onClose={onClose} size="md"
      title={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Icon n="copy" s={18} />{t("Импорт текста")}</span>}
      footer={<>
        <Button onClick={onClose}>{t("Отмена")}</Button>
        <Button variant="primary" disabled={!parsed.length} onClick={() => onImport(parsed)} style={{ flexGrow: 2 }}>
          {t("Импортировать")} {parsed.length > 0 ? `(${parsed.length})` : ''}
        </Button>
      </>}>
      <div style={{ fontSize: 13, color: C.mid, marginBottom: 14, lineHeight: 1.5 }}>
        {t("Вставьте слова и переводы. Можно начать строку с таймкода")} <span style={{ fontFamily: 'monospace', color: C.blueDark }}>0:30</span> {t("чтобы задать время карточки.")}
      </div>
      <Field multiline rows={6} aria-label={t("Импорт текста")} value={text} onChange={(e) => setText(e.target.value)}
        placeholder={t("apple\tяблоко\n0:30\tcat\tкот\n1:15\tdog\tсобака")} inputStyle={{ fontFamily: 'monospace', minHeight: 140 }} />
      <div style={{ marginBottom: 14 }}>
        <div style={caps(C)}>{t("Разделитель (между словом и переводом)")}</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[['comma', t("Запятая (,)")], ['custom', t("Другой")]].map(([val, lbl]) => (
            <Chip key={val} selected={sep === val} onClick={() => setSep(val)}>{lbl}</Chip>
          ))}
        </div>
        {sep === 'custom' && (
          <Field aria-label={t("Введите разделитель")} value={customSep} onChange={(e) => setCustomSep(e.target.value)} placeholder={t("Введите разделитель")}
            autoFocus style={{ marginTop: 8, marginBottom: 0, maxWidth: 220 }} />
        )}
      </div>
      <div>
        <div style={caps(C)}>{t("Предпросмотр —")} {nCards(parsed.length)}</div>
        {parsed.length === 0 ? <div style={{ fontSize: 13, color: C.faint, fontStyle: 'italic' }}>{t("Вставьте текст выше")}</div> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {parsed.slice(0, 10).map((c, i) => (
              <div key={i} style={{ background: C.surface, borderRadius: 9, padding: '7px 10px', display: 'flex', gap: 10, alignItems: 'center', minWidth: 0 }}>
                <span style={{ fontSize: 14, color: C.text, flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{c.word}</span>
                {c.translation && <><span style={{ color: C.faint, fontSize: 12 }}>→</span><span style={{ fontSize: 13, color: C.mid, flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{c.translation}</span></>}
              </div>
            ))}
            {parsed.length > 10 && <div style={{ fontSize: 12, color: C.faint, textAlign: 'center', padding: '4px 0' }}>{t("...и ещё")} {parsed.length - 10}</div>}
          </div>
        )}
      </div>
    </Sheet>
  );
}
